import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  UserCheck, AlertTriangle, MessageSquare, RefreshCw,
  Search, Filter, ArrowRight, ClipboardCheck,
} from 'lucide-react';
import StatTile from './StatTile';
import Attendance from './Attendance';
import AbsenceReasons from './AbsenceReasons';
import TrainingFeedback from './TrainingFeedback';
// Post-course quiz sittings — the fourth side of the same records.
import QuizResults from './QuizResults';
// The GPS evidence behind one check-in — the very panel the management
// Training Records tab opens (the badge is clickable
// here too). Shared component, so the two surfaces cannot drift apart.
import AttendanceLocationModal from './AttendanceLocationModal';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDateRange } from '../../utils/dateRange';
import { API_BASE_URL } from '../../utils/api';

// One tab for the three cross-session participation views:
// Attendance / Absence Reasons / Training Feedback are the same records seen
// from three sides — came /
// excused / rated — so this container owns the shared filters (search,
// session, session-date range) and the data, and the sub-components keep only
// what is genuinely theirs: the reason breakdown, the department filter, the
// Excel export. Attendance rows come down from LDDashboard, which already
// fetches /api/ld/attendance for the Overview cards — no second request.

const ParticipationRecords = ({
  attendance,
  attendanceLoading,
  onRefreshAttendance,
}) => {
  // The location endpoint sits behind ld:training:edit, so only a reader who
  // holds it gets a clickable badge — everyone else keeps the plain verdict
  // and its distance. Offering the click to an ld:view-only reader would only
  // buy them a 403.
  const { hasPermission } = useAuth();
  const canSeeLocation = hasPermission('ld:training:edit');
  // The one attendance row whose GPS evidence is open.
  const [locationRecordId, setLocationRecordId] = useState(null);

  const [absences, setAbsences] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  // The managed Absence Reason list — the reason filter offers every
  // category the firm maintains, not only the ones already used.
  const [reasonList, setReasonList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [activeSub, setActiveSub] = useState('attendance');

  // Shared filters — one set for all three sub-tabs. Every record type keys
  // them off the SESSION's title/code/date (an absence filters by the day of
  // the session it excuses, not by when the excuse was filed).
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSession, setFilterSession] = useState('all');
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);

  const fetchLocal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [absRes, fbRes, quizRes, reasonRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ld/absence-reasons`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/ld/training-feedback`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/ld/quiz-results`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/settings/lookups/ld_absence_reason?active=true`, { credentials: 'include' }),
      ]);
      if (!absRes.ok || !fbRes.ok || !quizRes.ok) {
        const failed = [absRes, fbRes, quizRes].find((r) => !r.ok);
        const errorData = await failed.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch participation records');
      }
      const absData = await absRes.json();
      const fbData = await fbRes.json();
      const quizData = await quizRes.json();
      if (absData.success) setAbsences(absData.data);
      if (fbData.success) setFeedback(fbData.data);
      if (quizData.success) setQuizResults(quizData.data);
      setLastUpdated(new Date());
      // The list is a convenience for the reason filter — a failure there
      // must not take the tab down with it.
      if (reasonRes.ok) {
        const list = await reasonRes.json();
        setReasonList((list.options || list.data || []).map((o) => o.value));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching participation records:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocal(); }, [fetchLocal]);

  const handleRefresh = () => {
    fetchLocal();
    if (onRefreshAttendance) onRefreshAttendance();
  };

  // ── Shared filtering ────────────────────────────────────────────────────────

  const inRange = useCallback((iso) => {
    // A record with no session date stays visible — same lenience the three
    // tabs applied individually before the merge.
    if (!iso) return true;
    if (startDate && iso < startDate) return false;
    if (endDate && iso > endDate) return false;
    return true;
  }, [startDate, endDate]);

  const matchesShared = useCallback((r, extraFields) => {
    if (filterSession !== 'all' && r.courseTitle !== filterSession) return false;
    if (!inRange(r.date)) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hay = [r.email, r.courseTitle, r.sessionCode];
      if (extraFields) extraFields.forEach((f) => hay.push(r[f]));
      if (!hay.some((v) => v && String(v).toLowerCase().includes(q))) return false;
    }
    return true;
  }, [filterSession, inRange, searchTerm]);

  // The Attendance sub-tab is about who was THERE: quiz credits (expected,
  // absent, passed the quiz — hours only) stay on the hours tabs and out of it.
  const filteredAttendance = useMemo(
    () => (attendance || []).filter((r) => r.creditSource !== 'quiz' && matchesShared(r)),
    [attendance, matchesShared]
  );
  const filteredAbsences = useMemo(
    // Reason text stays searchable — the old tab's search covered it.
    () => absences.filter((r) => matchesShared(r, ['reasonCategory', 'reasonDetail'])),
    [absences, matchesShared]
  );
  const filteredFeedback = useMemo(
    () => feedback.filter((r) => matchesShared(r)),
    [feedback, matchesShared]
  );
  const filteredQuiz = useMemo(
    // The paper's title and the person's name are searchable too.
    () => quizResults.filter((r) => matchesShared(r, ['quizTitle', 'name'])),
    [quizResults, matchesShared]
  );

  // Session dropdown options: every title seen by any of the four datasets.
  const sessionOptions = useMemo(() => {
    const titles = new Set();
    (attendance || []).forEach((r) => r.courseTitle && titles.add(r.courseTitle));
    absences.forEach((r) => r.courseTitle && titles.add(r.courseTitle));
    feedback.forEach((r) => r.courseTitle && titles.add(r.courseTitle));
    quizResults.forEach((r) => r.courseTitle && titles.add(r.courseTitle));
    return ['all', ...[...titles].sort()];
  }, [attendance, absences, feedback, quizResults]);

  // ── Cross-table figures the three separate tabs could never show ───────────
  // Record-level, within the current filters. Trainer bookings are not
  // attendances, so they stay out of every rate.
  const attendedCount = useMemo(
    () => filteredAttendance.filter((r) => !r.isTrainer).length,
    [filteredAttendance]
  );
  const absentCount = filteredAbsences.length;
  const responseCount = filteredFeedback.length;
  const attendanceRate = attendedCount + absentCount > 0
    ? `${((attendedCount / (attendedCount + absentCount)) * 100).toFixed(0)}%`
    : '—';
  const feedbackRate = attendedCount > 0
    ? `${((responseCount / attendedCount) * 100).toFixed(0)}%`
    : '—';

  const subTabs = [
    { id: 'attendance', label: `Attendance (${filteredAttendance.length})`, icon: UserCheck },
    { id: 'absences', label: `Absence Reasons (${filteredAbsences.length})`, icon: AlertTriangle },
    { id: 'feedback', label: `Training Feedback (${filteredFeedback.length})`, icon: MessageSquare },
    { id: 'quiz', label: `Quiz Results (${filteredQuiz.length})`, icon: ClipboardCheck },
  ];

  const hasActiveFilters = Boolean(searchTerm) || filterSession !== 'all' || Boolean(startDate) || Boolean(endDate);
  const anyLoading = loading || attendanceLoading;

  // ── Loading / error states ──────────────────────────────────────────────────

  if (anyLoading && (attendance || []).length === 0 && absences.length === 0 && feedback.length === 0
      && quizResults.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading participation records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="text-red-800">
          <h3 className="font-semibold">Error loading participation records</h3>
          <p className="text-sm mt-1">{error}</p>
        </div>
        <button
          onClick={fetchLocal}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Attendance, absences, feedback and quiz results across all training sessions"
        lastUpdated={lastUpdated}
        actions={
          <button
            onClick={handleRefresh}
            disabled={anyLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${anyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Cross-table figures for the filtered scope */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatTile label="Attended" value={attendedCount} tone="green" />
        <StatTile label="Absent (Filed)" value={absentCount} tone={absentCount > 0 ? 'amber' : 'slate'} />
        <StatTile
          label="Attendance Rate"
          value={attendanceRate}
          tone={attendanceRate === '—' ? 'slate' : (absentCount > 0 ? 'amber' : 'green')}
        />
        <StatTile label="Feedback Responses" value={responseCount} tone="slate" />
        <StatTile label="Response Rate" value={feedbackRate} tone="slate" />
        <StatTile label="Quiz Results" value={filteredQuiz.length} tone="slate" />
      </div>

      {/* Shared filters — apply to all three sub-tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by email, session, or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={filterSession}
              onChange={(e) => setFilterSession(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              {sessionOptions.map((session) => (
                <option key={session} value={session}>
                  {session === 'all' ? 'All Sessions' : session}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Session-date range on its own row — see CPDRecords for the why. */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-medium text-gray-700">
            Session Date
          </span>
          <label htmlFor="part-date-from" className="text-sm text-gray-600">From</label>
          <input
            id="part-date-from"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <ArrowRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <label htmlFor="part-date-to" className="text-sm text-gray-600">To</label>
          <input
            id="part-date-to"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {hasActiveFilters && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {filteredAttendance.length} of {(attendance || []).length} attendance,{' '}
              {filteredAbsences.length} of {absences.length} absences,{' '}
              {filteredFeedback.length} of {feedback.length} feedback,{' '}
              {filteredQuiz.length} of {quizResults.length} quiz results
            </span>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterSession('all');
                setStartDate('');
                setEndDate('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        {subTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveSub(t.id)}
              className={`pb-2 text-sm font-medium flex items-center gap-1.5 ${
                activeSub === t.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeSub === 'attendance' && (
        <Attendance
          records={filteredAttendance}
          onShowLocation={canSeeLocation ? setLocationRecordId : null}
        />
      )}
      {activeSub === 'absences' && <AbsenceReasons records={filteredAbsences} reasonList={reasonList} />}
      {activeSub === 'feedback' && <TrainingFeedback records={filteredFeedback} />}
      {activeSub === 'quiz' && <QuizResults records={filteredQuiz} />}

      {locationRecordId && (
        <AttendanceLocationModal
          recordId={locationRecordId}
          onClose={() => setLocationRecordId(null)}
        />
      )}
    </div>
  );
};

export default ParticipationRecords;
